"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";

// Define types for custom events
interface RouteChangeStartEvent extends CustomEvent<string> {
  detail: string;
}

interface RouteChangeCompleteEvent extends CustomEvent<string> {
  detail: string;
}

// Define types for router navigation options
interface NavigationOptions {
  scroll?: boolean;
  shallow?: boolean;
}

export function NavigationEvents() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  /** Bubble phase so child handlers (e.g. useNavigateOnTapOnly preventDefault) run first — avoids stuck loading overlay on reels. */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const target = e.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!anchor || anchor.getAttribute("target") === "_blank" || anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (href && href.startsWith("/")) {
        document.dispatchEvent(
          new CustomEvent<string>("routeChangeStart", { detail: href }) as RouteChangeStartEvent
        );
      }
    };

    document.addEventListener("click", handleClick, false);
    return () => document.removeEventListener("click", handleClick, false);
  }, []);

  // Handle programmatic navigation
  useEffect(() => {
    const r = router as { push: typeof router.push; replace: typeof router.replace };
    const originalPush = r.push.bind(router);
    const originalReplace = r.replace.bind(router);

    // Use the NavigationOptions interface defined at the top

    // Extending the router object
    r.push = (href: string, options?: NavigationOptions) => {
      // Dispatch routeChangeStart event
      document.dispatchEvent(
        new CustomEvent<string>("routeChangeStart", { detail: href }) as RouteChangeStartEvent
      );

      // Call the original method
      return originalPush(href, options);
    };

    // Extending the router object
    r.replace = (href: string, options?: NavigationOptions) => {
      // Dispatch routeChangeStart event
      document.dispatchEvent(
        new CustomEvent<string>("routeChangeStart", { detail: href }) as RouteChangeStartEvent
      );

      // Call the original method
      return originalReplace(href, options);
    };

    return () => {
      r.push = originalPush;
      r.replace = originalReplace;
    };
  }, [router]);

  // Handle navigation completion
  useEffect(() => {
    const url = `${pathname}${searchParams}`;
    document.dispatchEvent(
      new CustomEvent<string>("routeChangeComplete", { detail: url }) as RouteChangeCompleteEvent
    );
  }, [pathname, searchParams]);

  return null;
}
